import React, { useMemo } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getAvailableProducts,
  getRoadmapProducts,
  type CommercialProduct,
  type RoadmapProductStatus,
} from '../config/commercialProducts';
import { commercialPlans } from '../config/commercialPlans';
import { homeButtonClasses } from '../components/home/HomeButton';
import { HomeSectionHeader } from '../components/home/HomeSectionHeader';
import { HOME_SURFACES } from '../components/home/homeSurfaces';
import { MarketingPageCta } from '../components/marketing/MarketingPageCta';
import { MarketingPageHero } from '../components/marketing/MarketingPageHero';
import {
  marketingCardClass,
  marketingPlanBadgeClass,
  marketingStatCardClass,
} from '../components/marketing/marketingSurfaces';
import { useSectionViewTracking } from '../lib/analytics';
import { usePageMetadata } from '../lib/seo';
import { cn } from '../lib/utils';

const ROADMAP_LINK_ICON_SIZE = 16;
const ROADMAP_STATUS_LABEL_KEYS: Record<RoadmapProductStatus, string> = {
  alpha: 'common:common.alpha',
  coming_soon: 'common:common.comingSoon',
};

export function ModulesPage(): React.JSX.Element {
  const { t } = useTranslation(['modules', 'pricing', 'common', 'proof']);

  usePageMetadata({
    title: t('modules:meta.title'),
    description: t('modules:meta.description'),
    path: '/modulos',
  });

  const financeCoreRef = useSectionViewTracking('modules_finance_core');

  const availableProducts = getAvailableProducts();
  const roadmapProducts = getRoadmapProducts();

  const planLabelByCode = useMemo(() => {
    return new Map(commercialPlans.map((plan) => [plan.code, t(plan.nameKey)]));
  }, [t]);

  const resolvePlansForModule = (product: CommercialProduct): string[] => {
    return commercialPlans
      .filter(
        (plan) =>
          plan.includesModules.includes(product.code) ||
          plan.comingSoonModules.includes(product.code)
      )
      .map((plan) => planLabelByCode.get(plan.code) ?? plan.code);
  };

  return (
    <div className="flex flex-col bg-brand-bg">
      <MarketingPageHero
        badge={t('common:nav.modules')}
        title={t('modules:title')}
        subtitle={t('modules:subtitle')}
        actions={
          <>
            <Link
              to="/precios#demo-form"
              className={homeButtonClasses('accent', 'lg')}
            >
              {t('common:cta.requestDemo')}
            </Link>
            <a
              href="#available-modules"
              className={homeButtonClasses('outline', 'lg')}
            >
              {t('modules:hero.viewActive')}
            </a>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className={marketingStatCardClass}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
              {t('modules:hero.coreKicker')}
            </p>
            <h2 className="mt-3 font-serif text-2xl font-bold text-brand-primary">
              {t('proof:modules.financeCoreCalloutTitle')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
              {t('proof:modules.financeCoreCalloutBody')}
            </p>
          </div>
          <div className={marketingStatCardClass}>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
              {t('modules:hero.continuityBadge')}
            </p>
            <h2 className="mt-3 font-serif text-2xl font-bold text-brand-primary">
              {t('proof:modules.traceabilityCallout')}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-[var(--ir-muted)]">
              {t('modules:hero.continuityBody')}
            </p>
          </div>
        </div>
      </MarketingPageHero>

      <section
        id="available-modules"
        ref={financeCoreRef}
        className={HOME_SURFACES.ivory}
      >
        <div className="section-padding !pt-0">
          <HomeSectionHeader
            badge={t('modules:available')}
            title={t('modules:available')}
            subtitle={t('modules:availableSubtitle')}
            className="mb-12 md:mb-16"
          />

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {availableProducts.map((product) => (
              <article key={product.code} className={cn(marketingCardClass, 'p-8')}>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">
                  {t('common:common.available')}
                </p>
                <h3 className="mt-4 font-serif text-3xl font-bold text-brand-primary">
                  {t(product.nameKey)}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-[var(--ir-muted)]">
                  {t(product.descriptionKey)}
                </p>
                <p className="mt-6 text-sm font-semibold text-brand-teal">
                  {t(product.valueKey)}
                </p>

                <div className="mt-8 flex flex-wrap gap-2">
                  {resolvePlansForModule(product).map((planName) => (
                    <span
                      key={`${product.code}-${planName}`}
                      className={marketingPlanBadgeClass}
                    >
                      {t('modules:includedIn', { plan: planName })}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <HomeSectionHeader
            badge={t('modules:badges.roadmap')}
            title={t('modules:comingSoon')}
            subtitle={t('modules:comingSoonSubtitle')}
            className="mb-12 md:mb-16"
          />

          <div className={cn(marketingCardClass, 'divide-y divide-brand-primary/10')}>
            {roadmapProducts.map((product) => (
              <article
                key={product.code}
                className="flex flex-col justify-between gap-6 p-8 md:flex-row md:items-end"
              >
                <div className="max-w-3xl">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-accent-ink">
                    {t(ROADMAP_STATUS_LABEL_KEYS[product.status])}
                  </p>
                  <h3 className="mt-3 font-serif text-3xl font-bold text-brand-primary">
                    {t(product.nameKey)}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-[var(--ir-muted)]">
                    {t(product.descriptionKey)}
                  </p>
                  <p className="mt-5 text-sm font-semibold text-brand-teal">
                    {t(product.valueKey)}
                  </p>
                  {product.siteUrl ? (
                    <a
                      href={product.siteUrl}
                      className="mt-2 inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-brand-primary underline underline-offset-4"
                    >
                      {new URL(product.siteUrl).hostname}
                      <ArrowUpRight
                        aria-hidden="true"
                        size={ROADMAP_LINK_ICON_SIZE}
                      />
                    </a>
                  ) : null}
                </div>

                <div className="shrink-0 text-right">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
                    {t('modules:etaLabel')}
                  </p>
                  <p className="mt-3 font-serif text-xl font-bold text-brand-primary">
                    {product.availabilityDate ?? t('modules:etaPending')}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.ivory}>
        <div className="section-padding">
          <div className={cn(marketingCardClass, 'p-8 md:p-12')}>
            <HomeSectionHeader
              badge={t('modules:badges.activation')}
              title={t('modules:activation.title')}
              subtitle={t('modules:activation.subtitle')}
              className="mb-10"
            />
            <ul className="space-y-4 text-base leading-relaxed text-[var(--ir-muted)]">
              <li>{t('modules:activation.items.configDriven')}</li>
              <li>{t('modules:activation.items.planControlled')}</li>
              <li>{t('modules:activation.items.progressiveRelease')}</li>
            </ul>
          </div>
        </div>
      </section>

      <MarketingPageCta
        badge={t('modules:badges.nextStep')}
        title={t('modules:cta.title')}
        subtitle={t('modules:cta.subtitle')}
        actions={
          <Link
            to="/precios#demo-form"
            className={homeButtonClasses('accent', 'lg')}
          >
            {t('modules:cta.button')}
          </Link>
        }
      />
    </div>
  );
}
